/**
 * Type definitions for league-specific official roles and payment configuration
 *
 * This module defines the new structure for managing official roles that vary by league.
 * Each league can have its own set of roles with custom names, translations, and payment rules.
 */

/**
 * Category of official role
 * - arbitre: Referees (e.g., central referee, assistant referees)
 * - delegue: Delegates/Commissioners (e.g., match delegate, security delegate)
 * - auditeur: Auditors (stadium audit missions)
 */
export type OfficialRoleCategory = 'arbitre' | 'delegue' | 'auditeur';

/**
 * League-specific official role definition
 */
export interface LeagueOfficialRole {
  id: string;
  leagueId: string;
  /** Unique key for this role within the league (e.g., 'arbitre_central', 'delegue_principal') */
  roleKey: string;
  /** Category: arbitre or delegue */
  roleCategory: OfficialRoleCategory;
  /** Order in which roles are displayed in UI and documents */
  displayOrder: number;
  /** French name for the role */
  nameFr: string;
  /** Arabic name for the role */
  nameAr: string;
  /** Whether this role is currently active */
  isActive: boolean;
  /** Whether this role must be assigned for match validation */
  isRequired: boolean;
  createdAt: string;
  createdBy: string | null;
  updatedAt: string;
  updatedBy: string | null;
}

/**
 * Payment rule for a specific role in a specific league
 * Supports temporal validity for historical tracking
 *
 * When `leagueGroupId` is non-null, this rule is a per-cup override that wins
 * over the league-wide rule (with `leagueGroupId === null`) for any match that
 * belongs to that cup group. Each amount field is independent: NULL on a cup
 * row means "inherit the league-wide value for this field".
 */
export interface LeaguePaymentRule {
  id: string;
  leagueId: string;
  roleId: string;
  /** Cup group this rule overrides (kind='cup'). NULL = league-wide default. */
  leagueGroupId: string | null;
  /**
   * Age category this rule applies to (matches of groups of that category).
   * Exclusive with `leagueGroupId`; both NULL = league-wide default.
   * Precedence: group > category > league (see paymentRateResolution.ts).
   */
  categoryId: string | null;
  /** Base indemnity amount. NULL only for group / category overrides (inherits). */
  indemnityAmount: number | null;
  /** Per-km travel rate. NULL = inherit (league row) or no per-km rate. */
  distanceKmRate: number | null;
  /** Start date for this payment rule */
  validFrom: string;
  /** End date for this payment rule (null means currently active) */
  validTo: string | null;
  /** Whether this rule is active */
  isActive: boolean;
  createdAt: string;
  createdBy: string | null;
  updatedAt: string;
  updatedBy: string | null;
}

/**
 * Combined view of role with its current payment rule
 */
export interface LeagueRoleWithPayment extends LeagueOfficialRole {
  /** Current active payment rule ID (if exists) */
  paymentRuleId: string | null;
  /** Current indemnity amount */
  indemnityAmount: number | null;
  /** Current distance rate */
  distanceKmRate: number | null;
  /** League name for display */
  leagueName: string;
  /** League name in Arabic for display */
  leagueNameAr: string;
}

/**
 * Combined view of (role × cup group) with that cup's override (if any).
 * NULL `indemnityAmount` / `distanceKmRate` means the cup inherits the
 * league-wide value for that field.
 */
export interface LeagueGroupRoleWithPayment extends LeagueOfficialRole {
  /** Cup group this row belongs to (always kind='cup'). */
  leagueGroupId: string;
  /** Cup display name (`league_groups.name`). */
  leagueGroupName: string;
  /** Human-facing competition label (e.g., "Play-off Sud Ouest 2025-2026"). */
  organizedCompetitionName: string | null;
  /** Cup-specific payment rule ID (null when no override exists yet). */
  paymentRuleId: string | null;
  /** Cup-override indemnity amount (NULL = inherit league rate). */
  indemnityAmount: number | null;
  /** Cup-override distance rate (NULL = inherit league rate). */
  distanceKmRate: number | null;
  leagueName: string;
  leagueNameAr: string;
}

/**
 * Input for creating/updating a league role
 */
export interface LeagueOfficialRoleInput {
  id?: string;
  leagueId: string;
  roleKey: string;
  roleCategory: OfficialRoleCategory;
  displayOrder?: number;
  nameFr: string;
  nameAr: string;
  isActive?: boolean;
  isRequired?: boolean;
}

/**
 * Input for creating/updating a payment rule.
 *
 * When `leagueGroupId` is provided, the rule is scoped to that cup group and
 * either field may be NULL to inherit the league-wide value. When omitted,
 * the rule is league-wide and `indemnityAmount` is required.
 */
export interface LeaguePaymentRuleInput {
  id?: string;
  leagueId: string;
  roleId: string;
  /** Cup group this override applies to. Omit / null for league-wide rule. */
  leagueGroupId?: string | null;
  /** Age category this override applies to (exclusive with leagueGroupId). Insert only. */
  categoryId?: string | null;
  /**
   * Required for league-wide rules; nullable for group / category overrides.
   * On update, `undefined` leaves the column untouched and `null` clears it.
   */
  indemnityAmount?: number | null;
  distanceKmRate?: number | null;
  validFrom?: string;
  validTo?: string | null;
  isActive?: boolean;
}

/**
 * Active category-scoped payment rule (league_payment_rules.category_id set).
 * NULL fields inherit the league-wide value.
 */
export interface LeagueCategoryPaymentRule {
  id: string;
  leagueId: string;
  roleId: string;
  categoryId: string;
  indemnityAmount: number | null;
  distanceKmRate: number | null;
}

/**
 * Grouped roles by category for display
 */
export interface GroupedLeagueRoles {
  arbitres: LeagueRoleWithPayment[];
  delegues: LeagueRoleWithPayment[];
  auditeurs: LeagueRoleWithPayment[];
}

/**
 * Helper to group roles by category
 */
export function groupRolesByCategory(roles: LeagueRoleWithPayment[]): GroupedLeagueRoles {
  return roles.reduce(
    (acc, role) => {
      if (role.roleCategory === 'arbitre') {
        acc.arbitres.push(role);
      } else if (role.roleCategory === 'auditeur') {
        acc.auditeurs.push(role);
      } else {
        acc.delegues.push(role);
      }
      return acc;
    },
    { arbitres: [], delegues: [], auditeurs: [] } as GroupedLeagueRoles
  );
}

/**
 * Helper to get role translation based on language
 */
export function getRoleTranslation(role: LeagueOfficialRole, language: 'fr' | 'ar'): string {
  return language === 'ar' ? role.nameAr : role.nameFr;
}

/**
 * Helper to create role key from French name
 * Converts "Arbitre Central" -> "arbitre_central"
 */
export function createRoleKey(nameFr: string): string {
  return nameFr
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '') // Remove diacritics
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_|_$/g, '');
}

/**
 * Role translation map for backward compatibility
 * Maps old role names to { nameFr, nameAr }
 */
export const DEFAULT_ROLE_TRANSLATIONS: Record<string, { nameFr: string; nameAr: string; category: OfficialRoleCategory }> = {
  'Arbitre Central': {
    nameFr: 'Arbitre Central',
    nameAr: 'حكم ساحة',
    category: 'arbitre'
  },
  'Arbitre Assistant 1': {
    nameFr: 'Arbitre Assistant 1',
    nameAr: 'مساعد حكم 1',
    category: 'arbitre'
  },
  'Arbitre Assistant 2': {
    nameFr: 'Arbitre Assistant 2',
    nameAr: 'مساعد حكم 2',
    category: 'arbitre'
  },
  'Arbitre 4ème': {
    nameFr: 'Arbitre 4ème',
    nameAr: 'حكم رابع',
    category: 'arbitre'
  },
  'Délégué Principal': {
    nameFr: 'Délégué Principal',
    nameAr: 'محافظ اللقاء',
    category: 'delegue'
  },
  'Délégué Adjoint': {
    nameFr: 'Délégué Adjoint',
    nameAr: 'محافظ الأمن',
    category: 'delegue'
  },
  'Auditeur Principal': {
    nameFr: 'Auditeur Principal',
    nameAr: 'المراجع الرئيسي',
    category: 'auditeur'
  },
  'Auditeur Adjoint': {
    nameFr: 'Auditeur Adjoint',
    nameAr: 'نائب المراجع',
    category: 'auditeur'
  }
};
